import { useAudio } from '../../stores/audio.store';

interface AudioSettingsProps {
  className?: string;
  compact?: boolean;
}

export function AudioSettings({
  className = '',
  compact = false,
}: AudioSettingsProps) {
  const {
    masterVolume,
    sfxVolume,
    musicVolume,
    sfxEnabled,
    musicEnabled,
    setMasterVolume,
    setSfxVolume,
    setMusicVolume,
    toggleSfx,
    toggleMusic,
    play,
  } = useAudio();

  const handleVolumeChange = (
    setter: (v: number) => void,
    value: number,
    testSound?: Parameters<typeof play>[0]
  ) => {
    setter(value);
    if (testSound) {
      setTimeout(() => play(testSound), 50);
    }
  };

  if (compact) {
    return (
      <div className={`flex items-center gap-3 ${className}`}>
        <button
          onClick={() => {
            toggleSfx();
            play('menuSelect');
          }}
          className={`p-2 rounded-lg transition-colors ${
            sfxEnabled
              ? 'text-purple-500 dark:text-purple-400 hover:bg-purple-100 dark:hover:bg-purple-900/30'
              : 'text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
          }`}
          title={sfxEnabled ? 'Mute sound effects' : 'Enable sound effects'}
        >
          {sfxEnabled ? <SpeakerIcon /> : <SpeakerOffIcon />}
        </button>
        <button
          onClick={() => {
            toggleMusic();
            play('menuSelect');
          }}
          className={`p-2 rounded-lg transition-colors ${
            musicEnabled
              ? 'text-purple-500 dark:text-purple-400 hover:bg-purple-100 dark:hover:bg-purple-900/30'
              : 'text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
          }`}
          title={musicEnabled ? 'Mute music' : 'Enable music'}
        >
          {musicEnabled ? <MusicIcon /> : <MusicOffIcon />}
        </button>
      </div>
    );
  }

  return (
    <div
      className={`space-y-4 p-4 rounded-lg bg-slate-100 dark:bg-slate-800 ${className}`}
    >
      <h3 className="text-sm font-semibold text-slate-900 dark:text-white uppercase tracking-wider">
        Audio Settings
      </h3>

      {/* Master Volume */}
      <VolumeSlider
        label="Master Volume"
        value={masterVolume}
        onChange={(v) => handleVolumeChange(setMasterVolume, v)}
      />

      {/* SFX */}
      <div className="flex items-center justify-between">
        <VolumeSlider
          label="Sound Effects"
          value={sfxVolume}
          onChange={(v) => handleVolumeChange(setSfxVolume, v, 'blip')}
          disabled={!sfxEnabled}
          className="flex-1"
        />
        <button
          onClick={() => {
            toggleSfx();
            if (!sfxEnabled) play('menuSelect');
          }}
          className={`ml-3 p-2 rounded-lg transition-colors ${
            sfxEnabled
              ? 'text-purple-500 dark:text-purple-400 hover:bg-purple-200 dark:hover:bg-purple-900/30'
              : 'text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
          }`}
        >
          {sfxEnabled ? <SpeakerIcon /> : <SpeakerOffIcon />}
        </button>
      </div>

      {/* Music */}
      <div className="flex items-center justify-between">
        <VolumeSlider
          label="Music"
          value={musicVolume}
          onChange={(v) => handleVolumeChange(setMusicVolume, v)}
          disabled={!musicEnabled}
          className="flex-1"
        />
        <button
          onClick={toggleMusic}
          className={`ml-3 p-2 rounded-lg transition-colors ${
            musicEnabled
              ? 'text-purple-500 dark:text-purple-400 hover:bg-purple-200 dark:hover:bg-purple-900/30'
              : 'text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
          }`}
        >
          {musicEnabled ? <MusicIcon /> : <MusicOffIcon />}
        </button>
      </div>
    </div>
  );
}

interface VolumeSliderProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
  className?: string;
}

function VolumeSlider({
  label,
  value,
  onChange,
  disabled = false,
  className = '',
}: VolumeSliderProps) {
  return (
    <div className={className}>
      <label className="block text-sm text-slate-600 dark:text-slate-400 mb-1">
        {label}
      </label>
      <input
        type="range"
        min="0"
        max="1"
        step="0.05"
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        disabled={disabled}
        className={`
          w-full h-2 rounded-lg appearance-none cursor-pointer
          bg-slate-300 dark:bg-slate-600
          accent-purple-500 dark:accent-purple-400
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      />
    </div>
  );
}

// Icons
function SpeakerIcon() {
  return (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
      />
    </svg>
  );
}

function SpeakerOffIcon() {
  return (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2"
      />
    </svg>
  );
}

function MusicIcon() {
  return (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"
      />
    </svg>
  );
}

function MusicOffIcon() {
  return (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 3l18 18"
      />
    </svg>
  );
}
